import { useState, useEffect, useRef, useCallback } from 'react';
import { db, ensureAnonymousAuth } from './firebaseConfig';
import { storageKey } from './storageKeys';
import {
  ref, set, get, onValue, off, update, remove,
  onDisconnect, runTransaction, serverTimestamp,
} from 'firebase/database';

const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const SESSION_ROOM_KEY = storageKey('xapxam_roomCode');
const LOCAL_ROOM_KEY = storageKey('xapxam_lastRoomCode');
const LEADER_HEARTBEAT_MS = 5000;
const TAKEOVER_GRACE_MS = 15000;

function getSavedRoomCode() {
  return sessionStorage.getItem(SESSION_ROOM_KEY) || localStorage.getItem(LOCAL_ROOM_KEY);
}

function saveRoomCode(code) {
  if (!code) return;
  sessionStorage.setItem(SESSION_ROOM_KEY, code);
  localStorage.setItem(LOCAL_ROOM_KEY, code);
}

function clearSavedRoomCode() {
  sessionStorage.removeItem(SESSION_ROOM_KEY);
  localStorage.removeItem(LOCAL_ROOM_KEY);
}

function generateCode(len = 5) {
  let code = '';
  for (let i = 0; i < len; i++) {
    code += CHARS[Math.floor(Math.random() * CHARS.length)];
  }
  return code;
}

export default function useMultiplayer() {
  const [mode, setMode] = useState('solo'); // 'solo' | 'leader' | 'viewer'
  const [roomCode, setRoomCode] = useState(null);
  const [uid, setUid] = useState(null);
  const [connected, setConnected] = useState(false);
  const [remoteGameState, setRemoteGameState] = useState(null);
  const [leaderTransferRequest, setLeaderTransferRequest] = useState(null);
  const [viewerRequestStatus, setViewerRequestStatus] = useState('none');
  const [error, setError] = useState('');
  const [leaderConnected, setLeaderConnected] = useState(true);
  const [remotePlayerAvatars, setRemotePlayerAvatars] = useState(null);
  const [remoteSeatClaims, setRemoteSeatClaims] = useState(null);
  const [lastLeftRoomCode, setLastLeftRoomCode] = useState(null);
  const [rejoining, setRejoining] = useState(() => !!getSavedRoomCode());
  const [leaderHeartbeatAt, setLeaderHeartbeatAt] = useState(null);
  const [leaderDisconnectedAt, setLeaderDisconnectedAt] = useState(null);
  const [stateSyncStatus, setStateSyncStatus] = useState('idle');
  const [lastStateSyncedAt, setLastStateSyncedAt] = useState(null);

  const listenersRef = useRef([]);
  const roomCodeRef = useRef(null);
  const modeRef = useRef('solo');
  const uidRef = useRef(null);
  const remoteGameStateRef = useRef(null);
  const disconnectObservedAtRef = useRef(null);
  const syncAttemptRef = useRef(0);
  const pendingGameStateRef = useRef(null);
  const pushGameStateRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { roomCodeRef.current = roomCode; }, [roomCode]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { uidRef.current = uid; }, [uid]);
  useEffect(() => { remoteGameStateRef.current = remoteGameState; }, [remoteGameState]);

  // Persist active roomCode for refresh/browser-close recovery.
  useEffect(() => {
    if (roomCode) {
      saveRoomCode(roomCode);
    }
  }, [roomCode]);

  // Track Firebase connection status
  useEffect(() => {
    const connRef = ref(db, '.info/connected');
    onValue(connRef, (snap) => {
      setConnected(snap.val() === true);
    });
    return () => off(connRef);
  }, []);

  // Cleanup all listeners
  const cleanupListeners = useCallback(() => {
    listenersRef.current.forEach(({ ref: r }) => off(r));
    listenersRef.current = [];
  }, []);

  // Subscribe to a Firebase path
  const subscribe = useCallback((path, callback) => {
    const r = ref(db, path);
    onValue(r, callback);
    listenersRef.current.push({ ref: r });
    return r;
  }, []);

  const subscribeLeaderTransferRequest = useCallback((code) => {
    subscribe(`rooms/${code}/leaderTransferRequest`, (snap) => {
      const val = snap.val();
      if (val && modeRef.current === 'leader') {
        setLeaderTransferRequest(val);
      } else {
        setLeaderTransferRequest(null);
      }
    });
  }, [subscribe]);

  const subscribeViewerTransferResponse = useCallback((code, myUid) => {
    subscribe(`rooms/${code}/leaderTransferResponse`, (snap) => {
      const val = snap.val();
      if (!val || (val.toUid && val.toUid !== myUid)) return;
      if (val.accepted) {
        setViewerRequestStatus('accepted');
      } else {
        setViewerRequestStatus('rejected');
        setTimeout(() => setViewerRequestStatus('none'), 3000);
      }
      remove(ref(db, `rooms/${code}/leaderTransferResponse`)).catch(() => {});
    });
  }, [subscribe]);

  const subscribeViewerRoomState = useCallback((code, myUid) => {
    subscribe(`rooms/${code}/gameState`, (snap) => {
      const value = snap.val();
      if (value) {
        setRemoteGameState(value);
        setLastStateSyncedAt(value._sync?.updatedAt || null);
      }
    });

    subscribe(`rooms/${code}/leaderConnected`, (snap) => {
      const isLeaderConnected = snap.val() === true;
      setLeaderConnected(isLeaderConnected);
      if (isLeaderConnected) {
        disconnectObservedAtRef.current = null;
        setViewerRequestStatus(status => status === 'takeover-warning' ? 'none' : status);
      } else if (!disconnectObservedAtRef.current) {
        disconnectObservedAtRef.current = Date.now();
      }
    });

    subscribe(`rooms/${code}/leaderHeartbeatAt`, (snap) => {
      setLeaderHeartbeatAt(snap.val() || null);
    });

    subscribe(`rooms/${code}/leaderDisconnectedAt`, (snap) => {
      setLeaderDisconnectedAt(snap.val() || null);
    });

    subscribe(`rooms/${code}/disbanded`, (snap) => {
      if (snap.val() === true) {
        cleanupListeners();
        setMode('solo');
        setRoomCode(null);
        setRemoteGameState(null);
        clearSavedRoomCode();
        setError('The room dealer closed the room');
      }
    });

    subscribeViewerTransferResponse(code, myUid);
  }, [cleanupListeners, subscribe, subscribeViewerTransferResponse]);

  // Helper: keep dealer presence server-backed and reconnect-safe.
  const setupLeaderDisconnect = useCallback(async (code) => {
    const roomRef = ref(db, `rooms/${code}`);
    await update(roomRef, {
      leaderConnected: true,
      leaderHeartbeatAt: serverTimestamp(),
      leaderDisconnectedAt: null,
    });
    await onDisconnect(roomRef).update({
      leaderConnected: false,
      leaderDisconnectedAt: serverTimestamp(),
    });
  }, []);

  // Re-arm onDisconnect and presence whenever Firebase reconnects. A heartbeat
  // makes a stale boolean detectable without changing score state.
  useEffect(() => {
    if (!connected || mode !== 'leader' || !roomCode || !uid) return undefined;

    let stopped = false;
    const renewPresence = async () => {
      try {
        await setupLeaderDisconnect(roomCode);
        if (!stopped) {
          setLeaderConnected(true);
          setLeaderDisconnectedAt(null);
          if (pendingGameStateRef.current && pushGameStateRef.current) {
            pushGameStateRef.current(pendingGameStateRef.current);
          }
        }
      } catch (e) {
        if (!stopped) setError('Dealer connection could not be verified. Scoring is paused.');
      }
    };
    const heartbeat = () => {
      update(ref(db, `rooms/${roomCode}`), {
        leaderConnected: true,
        leaderHeartbeatAt: serverTimestamp(),
        leaderDisconnectedAt: null,
      }).catch(() => {});
    };

    renewPresence();
    const intervalId = window.setInterval(heartbeat, LEADER_HEARTBEAT_MS);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [connected, mode, roomCode, uid, setupLeaderDisconnect]);

  const refreshViewerPresence = useCallback(async (code, myUid) => {
    if (!code || !myUid) return;
    const viewerRef = ref(db, `rooms/${code}/viewers/${myUid}`);
    await update(viewerRef, { lastSeenAt: Date.now() });
    onDisconnect(viewerRef).remove();
  }, []);

  const becomeViewer = useCallback(async (code, myUid) => {
    setMode('viewer');
    setStateSyncStatus('idle');
    setLeaderTransferRequest(null);
    setViewerRequestStatus('none');
    if (myUid) {
      const viewerRef = ref(db, `rooms/${code}/viewers/${myUid}`);
      await set(viewerRef, { joinedAt: Date.now(), demotedAt: Date.now(), lastSeenAt: Date.now() });
      onDisconnect(viewerRef).remove();
      subscribeViewerRoomState(code, myUid);
    }
  }, [subscribeViewerRoomState]);

  const becomeLeader = useCallback(async (code, myUid) => {
    setMode('leader');
    setStateSyncStatus('syncing');
    setLeaderTransferRequest(null);
    setViewerRequestStatus('accepted');
    if (myUid) {
      remove(ref(db, `rooms/${code}/viewers/${myUid}`)).catch(() => {});
    }
    await setupLeaderDisconnect(code);
    setLeaderConnected(true);
    subscribeLeaderTransferRequest(code);
  }, [setupLeaderDisconnect, subscribeLeaderTransferRequest]);

  const subscribeLeaderUid = useCallback((code, myUid) => {
    subscribe(`rooms/${code}/leaderUid`, (snap) => {
      const leaderUid = snap.val();
      if (!leaderUid) {
        setLeaderConnected(false);
        return;
      }
      if (leaderUid === myUid && modeRef.current !== 'leader') {
        becomeLeader(code, myUid).catch(e => setError('Failed to become dealer: ' + e.message));
        return;
      }
      if (leaderUid !== myUid && modeRef.current === 'leader') {
        becomeViewer(code, myUid).catch(e => setError('Failed to transfer dealer: ' + e.message));
      }
    });
  }, [becomeLeader, becomeViewer, subscribe]);

  // Create room
  const createRoom = useCallback(async (gameState) => {
    try {
      setError('');
      setLastLeftRoomCode(null);
      const myUid = await ensureAnonymousAuth();
      setUid(myUid);

      // Generate unique code
      let code;
      for (let i = 0; i < 10; i++) {
        code = generateCode();
        const snap = await get(ref(db, `rooms/${code}`));
        if (!snap.exists()) break;
      }

      const roomRef = ref(db, `rooms/${code}`);
      const initialGameState = {
        ...(gameState || {}),
        _sync: {
          revision: 1,
          updatedAt: serverTimestamp(),
          leaderUid: myUid,
        },
      };
      await set(roomRef, {
        leaderUid: myUid,
        leaderConnected: true,
        leaderHeartbeatAt: serverTimestamp(),
        leaderDisconnectedAt: null,
        createdAt: Date.now(),
        gameState: initialGameState,
      });
      setStateSyncStatus('synced');
      setLastStateSyncedAt(Date.now());

      // Soft disconnect: mark dealer as disconnected instead of destroying room
      await setupLeaderDisconnect(code);

      // Initialize shared avatar path
      if (gameState && gameState.playerAvatars) {
        await set(ref(db, `rooms/${code}/playerAvatars`), gameState.playerAvatars);
      }

      setRoomCode(code);
      saveRoomCode(code);
      setMode('leader');
      setLeaderConnected(true);

      subscribeLeaderTransferRequest(code);
      subscribeLeaderUid(code, myUid);

      // Subscribe to shared player avatars
      subscribe(`rooms/${code}/playerAvatars`, (snap) => {
        setRemotePlayerAvatars(snap.val() || {});
      });

      // Subscribe to seat claims
      subscribe(`rooms/${code}/seatClaims`, (snap) => {
        setRemoteSeatClaims(snap.val() || {});
      });

      return code;
    } catch (e) {
      setError('Failed to create room: ' + e.message);
      return null;
    }
  }, [setupLeaderDisconnect, subscribe, subscribeLeaderTransferRequest, subscribeLeaderUid]);

  // Join room
  const joinRoom = useCallback(async (code) => {
    try {
      setError('');
      setLastLeftRoomCode(null);
      const normalized = code.trim().toUpperCase();
      const myUid = await ensureAnonymousAuth();
      setUid(myUid);

      const roomSnap = await get(ref(db, `rooms/${normalized}`));
      if (!roomSnap.exists()) {
        setError('Room not found');
        clearSavedRoomCode();
        return false;
      }

      const roomData = roomSnap.val();
      if (roomData.gameState?._sync?.updatedAt) {
        setLastStateSyncedAt(roomData.gameState._sync.updatedAt);
      }

      // If room was disbanded, don't join
      if (roomData.disbanded) {
        setError('This room has been closed');
        clearSavedRoomCode();
        return false;
      }

      // If we're actually the leader, rejoin as leader
      if (roomData.leaderUid === myUid) {
        setRoomCode(normalized);
        saveRoomCode(normalized);
        setMode('leader');
        // Re-establish connection flag and disconnect handler
        await setupLeaderDisconnect(normalized);
        setLeaderConnected(true);

        subscribeLeaderTransferRequest(normalized);
        subscribeLeaderUid(normalized, myUid);

        // Subscribe to shared player avatars
        subscribe(`rooms/${normalized}/playerAvatars`, (snap) => {
          setRemotePlayerAvatars(snap.val() || {});
        });

        // Subscribe to seat claims
        subscribe(`rooms/${normalized}/seatClaims`, (snap) => {
          setRemoteSeatClaims(snap.val() || {});
        });

        return true;
      }

      setRoomCode(normalized);
      saveRoomCode(normalized);
      setMode('viewer');

      // Set presence
      const viewerRef = ref(db, `rooms/${normalized}/viewers/${myUid}`);
      await set(viewerRef, { joinedAt: Date.now(), lastSeenAt: Date.now() });
      onDisconnect(viewerRef).remove();

      subscribeViewerRoomState(normalized, myUid);

      // Listen for leaderUid changes (in case we become leader)
      subscribeLeaderUid(normalized, myUid);

      // Subscribe to shared player avatars
      subscribe(`rooms/${normalized}/playerAvatars`, (snap) => {
        setRemotePlayerAvatars(snap.val() || {});
      });

      // Subscribe to seat claims
      subscribe(`rooms/${normalized}/seatClaims`, (snap) => {
        setRemoteSeatClaims(snap.val() || {});
      });

      return true;
    } catch (e) {
      setError('Failed to join room: ' + e.message);
      return false;
    }
  }, [subscribe, setupLeaderDisconnect, subscribeLeaderTransferRequest, subscribeLeaderUid, subscribeViewerRoomState]);

  // Auto-rejoin room on refresh/browser reopen (if a saved room code exists)
  const hasAttemptedRejoin = useRef(false);
  useEffect(() => {
    if (hasAttemptedRejoin.current) return;
    const savedCode = getSavedRoomCode();
    if (!savedCode) { setRejoining(false); return; }
    hasAttemptedRejoin.current = true;
    setRejoining(true);
    joinRoom(savedCode).then((success) => {
      if (!success) {
        clearSavedRoomCode();
      }
    }).catch(() => {
      clearSavedRoomCode();
    }).finally(() => {
      setRejoining(false);
    });
  }, [joinRoom]);

  // Leave room
  const leaveRoom = useCallback(async () => {
    if (!roomCodeRef.current) return;
    const code = roomCodeRef.current;

    try {
      if (modeRef.current === 'leader') {
        // Cancel the onDisconnect so it doesn't fire after we remove
        await onDisconnect(ref(db, `rooms/${code}`)).cancel();
        // Mark as disbanded so viewers get notified
        await update(ref(db, `rooms/${code}`), { disbanded: true });
        // Clean up room after short delay
        setTimeout(async () => {
          try { await remove(ref(db, `rooms/${code}`)); } catch (e) { /* ignore */ }
        }, 2000);
      } else if (uid) {
        await remove(ref(db, `rooms/${code}/viewers/${uid}`));
      }
    } catch (e) { /* ignore cleanup errors */ }

    // Save the room code for "rejoin" button (only if viewer — leader disbands the room)
    if (modeRef.current !== 'leader') {
      setLastLeftRoomCode(code);
    } else {
      setLastLeftRoomCode(null);
    }

    cleanupListeners();
    clearSavedRoomCode();
    setMode('solo');
    setRoomCode(null);
    setRemoteGameState(null);
    setLeaderTransferRequest(null);
    setViewerRequestStatus('none');
    setLeaderConnected(true);
    setRemotePlayerAvatars(null);
    setRemoteSeatClaims(null);
    setLeaderHeartbeatAt(null);
    setLeaderDisconnectedAt(null);
    setStateSyncStatus('idle');
    setLastStateSyncedAt(null);
    disconnectObservedAtRef.current = null;
    setError('');
  }, [uid, cleanupListeners]);

  // Push game state (dealer only)
  const pushGameState = useCallback(async (gameState) => {
    if (modeRef.current !== 'leader' || !roomCodeRef.current) return;
    pendingGameStateRef.current = gameState;
    const attempt = ++syncAttemptRef.current;
    setStateSyncStatus('syncing');
    try {
      const stateRef = ref(db, `rooms/${roomCodeRef.current}/gameState`);
      const result = await runTransaction(stateRef, (currentState) => {
        if (modeRef.current !== 'leader' || !uidRef.current) return;
        const currentRevision = Number(currentState?._sync?.revision || 0);
        return {
          ...gameState,
          _sync: {
            revision: currentRevision + 1,
            updatedAt: serverTimestamp(),
            leaderUid: uidRef.current,
          },
        };
      }, { applyLocally: false });

      if (!result.committed) throw new Error('Score state transaction was not committed');
      if (attempt === syncAttemptRef.current) {
        const syncedAt = result.snapshot.val()?._sync?.updatedAt;
        setLastStateSyncedAt(typeof syncedAt === 'number' ? syncedAt : Date.now());
        setStateSyncStatus('synced');
        setError('');
      }
    } catch (e) {
      if (attempt === syncAttemptRef.current) {
        setStateSyncStatus('error');
        setError('Scores are not synced. Reconnect before scoring or transferring dealer control.');
      }
    }
  }, []);
  pushGameStateRef.current = pushGameState;

  // Push avatar change (anyone can do this)
  const pushAvatarChange = useCallback(async (playerName, avatarId) => {
    if (!roomCodeRef.current) return;
    try {
      if (avatarId) {
        await set(ref(db, `rooms/${roomCodeRef.current}/playerAvatars/${playerName}`), avatarId);
      } else {
        await remove(ref(db, `rooms/${roomCodeRef.current}/playerAvatars/${playerName}`));
      }
    } catch (e) { /* silently fail */ }
  }, []);

  // Claim a seat (anyone can do this) — each UID can only claim one seat
  const claimSeat = useCallback(async (playerName, claimerUid) => {
    if (!roomCodeRef.current || !claimerUid) return;
    try {
      // First, get current claims to remove any existing claim by this UID
      const claimsSnap = await get(ref(db, `rooms/${roomCodeRef.current}/seatClaims`));
      const currentClaims = claimsSnap.val() || {};
      const updates = {};
      // Remove any previous claim by this UID
      for (const [name, uid] of Object.entries(currentClaims)) {
        if (uid === claimerUid && name !== playerName) {
          updates[`rooms/${roomCodeRef.current}/seatClaims/${name}`] = null;
        }
      }
      // Set the new claim
      updates[`rooms/${roomCodeRef.current}/seatClaims/${playerName}`] = claimerUid;
      await update(ref(db), updates);
    } catch (e) { /* silently fail */ }
  }, []);

  // Unclaim a seat
  const unclaimSeat = useCallback(async (playerName) => {
    if (!roomCodeRef.current) return;
    try {
      await remove(ref(db, `rooms/${roomCodeRef.current}/seatClaims/${playerName}`));
    } catch (e) { /* silently fail */ }
  }, []);

  // Request dealer (viewer only). Disconnected takeover is deliberately a
  // two-step, revision-checked action so a stale viewer cannot silently win.
  const requestLeader = useCallback(async (displayName, confirmTakeover = false) => {
    if (modeRef.current !== 'viewer' || !roomCodeRef.current || !uid) return;
    try {
      await refreshViewerPresence(roomCodeRef.current, uid);

      const roomRef = ref(db, `rooms/${roomCodeRef.current}`);
      // Prime the room path through a value event before a room-level
      // transaction. RTDB transactions begin from the local cache; a plain
      // get() can still leave that initial value null and abort a safe claim.
      const roomData = await new Promise((resolve, reject) => {
        onValue(roomRef, (snap) => resolve(snap.val()), reject, { onlyOnce: true });
      });
      if (!roomData) throw new Error('Room no longer exists');
      if (roomData.disbanded === true) throw new Error('Room has been closed');
      const isLeaderConn = roomData.leaderConnected === true;

      if (!isLeaderConn) {
        if (!confirmTakeover) {
          setViewerRequestStatus('takeover-warning');
          setError('');
          return;
        }

        const disconnectedAt = Number(
          roomData.leaderDisconnectedAt || disconnectObservedAtRef.current || Date.now()
        );
        const remainingMs = TAKEOVER_GRACE_MS - (Date.now() - disconnectedAt);
        if (remainingMs > 0) {
          setViewerRequestStatus('takeover-warning');
          setError(`Wait ${Math.ceil(remainingMs / 1000)}s for the dealer to reconnect before takeover.`);
          return;
        }

        const expectedLeaderUid = roomData.leaderUid;
        const serverRevision = Number(roomData.gameState?._sync?.revision || 0);
        const viewerRevision = Number(remoteGameStateRef.current?._sync?.revision || 0);
        if (serverRevision !== viewerRevision) {
          setViewerRequestStatus('takeover-warning');
          setError('Newer scores arrived. Review the updated standings before takeover.');
          return;
        }

        let abortReason = 'room state changed';
        let usedPrimedSnapshot = false;
        const result = await runTransaction(roomRef, (currentRoom) => {
          // The Web SDK can invoke a transaction with null before its cache is
          // hydrated. Seed that first pass from the just-confirmed value event;
          // the server still compares revisions and retries with current data.
          const candidateRoom = currentRoom || (!usedPrimedSnapshot ? roomData : null);
          usedPrimedSnapshot = true;
          if (!candidateRoom) {
            abortReason = 'room is unavailable';
            return;
          }
          if (candidateRoom.disbanded === true) {
            abortReason = 'room was closed';
            return;
          }
          if (candidateRoom.leaderConnected === true) {
            abortReason = 'dealer reconnected';
            return;
          }
          if (candidateRoom.leaderUid !== expectedLeaderUid) {
            abortReason = 'dealer already changed';
            return;
          }
          const currentRevision = Number(candidateRoom.gameState?._sync?.revision || 0);
          if (currentRevision !== serverRevision) {
            abortReason = 'scores changed';
            return;
          }
          return {
            ...candidateRoom,
            leaderUid: uid,
            leaderConnected: true,
            leaderHeartbeatAt: serverTimestamp(),
            leaderDisconnectedAt: null,
            leaderTransferRequest: null,
            leaderTransferResponse: null,
          };
        }, { applyLocally: false });

        if (!result.committed) {
          setViewerRequestStatus('none');
          setError(`Takeover cancelled: ${abortReason}.`);
          return;
        }

        await setupLeaderDisconnect(roomCodeRef.current);
        setViewerRequestStatus('accepted');
        setError('');
        return;
      }

      // Leader is connected — send request normally
      setViewerRequestStatus('pending');
      await set(ref(db, `rooms/${roomCodeRef.current}/leaderTransferRequest`), {
        fromUid: uid,
        fromName: displayName || 'A viewer',
        timestamp: Date.now(),
      });
    } catch (e) {
      setViewerRequestStatus('none');
      setError('Failed to request dealer: ' + e.message);
    }
  }, [uid, refreshViewerPresence, setupLeaderDisconnect]);

  const cancelLeaderRequest = useCallback(() => {
    setViewerRequestStatus('none');
    setError('');
  }, []);

  // Respond to dealer transfer request (dealer only)
  const respondToLeaderRequest = useCallback(async (accept) => {
    if (modeRef.current !== 'leader' || !roomCodeRef.current || !leaderTransferRequest) return;
    try {
      if (accept) {
        // Cancel our own onDisconnect before transferring
        await onDisconnect(ref(db, `rooms/${roomCodeRef.current}`)).cancel();

        await update(ref(db, `rooms/${roomCodeRef.current}`), {
          leaderUid: leaderTransferRequest.fromUid,
          leaderTransferRequest: null,
          leaderTransferResponse: {
            toUid: leaderTransferRequest.fromUid,
            accepted: true,
            timestamp: Date.now(),
          },
        });
      } else {
        await update(ref(db, `rooms/${roomCodeRef.current}`), {
          leaderTransferRequest: null,
          leaderTransferResponse: {
            toUid: leaderTransferRequest.fromUid,
            accepted: false,
            timestamp: Date.now(),
          },
        });
      }
      setLeaderTransferRequest(null);
    } catch (e) {
      setError('Failed to respond: ' + e.message);
    }
  }, [leaderTransferRequest]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupListeners();
    };
  }, [cleanupListeners]);

  return {
    mode,
    roomCode,
    connected,
    uid,
    remoteGameState,
    leaderTransferRequest,
    viewerRequestStatus,
    leaderConnected,
    leaderHeartbeatAt,
    leaderDisconnectedAt,
    stateSyncStatus,
    lastStateSyncedAt,
    remotePlayerAvatars,
    remoteSeatClaims,
    lastLeftRoomCode,
    rejoining,
    error,
    createRoom,
    joinRoom,
    leaveRoom,
    pushGameState,
    pushAvatarChange,
    claimSeat,
    unclaimSeat,
    requestLeader,
    cancelLeaderRequest,
    respondToLeaderRequest,
    clearLastLeftRoom: () => setLastLeftRoomCode(null),
  };
}
